import React, { PureComponent } from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import { pickBy } from 'ramda'

type IconProps = {
  style?: Object
  type: string
  className?: string
  color?: string
  size?: number
  onClick?: () => void
}

class Icon extends PureComponent<IconProps> {
  static Sizes = {
    large: 36,
    standard: 24,
    small: 18,
    smaller: 13,
  }

  static defaultProps = {
    color: undefined,
    size: 24,
  }

  static Types = {
    menu: 'menu',
    back: 'back',
    next: 'next',
    options: 'options',
    stream: 'stream',
    inbox: 'inbox',
    bookmark: 'bookmark',
    feeds: 'feeds',
    megaphone: 'megaphone',
    amplify: 'amplify',
    bell: 'bell',
    notify: 'notify',
    collections: 'collections',
    search: 'search',
    'search-small': 'search-small',
    compose: 'compose',
    create: 'create',
    manage: 'manage',
    edit: 'edit',
    submission: 'submission',
    published: 'published',
    file: 'file',
    content: 'content',
    draft: 'draft',
    'bar-graph': 'bar-graph',
    like: 'like',
    comment: 'comment',
    view: 'view',
    share: 'share',
    'like-uncolored': 'like-uncolored',
    'comment-uncolored': 'comment-uncolored',
    'view-uncolored': 'view-uncolored',
    'share-uncolored': 'share-uncolored',
    'like-old': 'like-old',
    'comment-old': 'comment-old',
    'share-old': 'share-old',
    'view-old': 'view-old',
    networks: 'networks',
    hub: 'hub',
    tags: 'tags',
    tag: 'tag',
    template: 'template',
    team: 'team',
    profile: 'profile',
    user: 'user',
    design: 'design',
    analytics: 'analytics',
    integrations: 'integrations',
    accounts: 'accounts',
    settings: 'settings',
    ctas: 'ctas',
    poll: 'poll',
    grid: 'grid',
    hubs: 'hubs',
    addons: 'addons',
    refresh: 'refresh',
    resend: 'resend',
    responses: 'responses',
    feature: 'feature',
    pin: 'pin',
    'hide-content': 'hide-content',
    camera: 'camera',
    'no-image': 'no-image',
    lock: 'lock',
    'add-image': 'add-image',
    'lead-image': 'lead-image',
    upload: 'upload',
    download: 'download',
    target: 'target',
    delete: 'delete',
    'delete-small': 'delete-small',
    calendar: 'calendar',
    clock: 'clock',
    time: 'time',
    warning: 'warning',
    title: 'title',
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'strikethrough',
    superscript: 'superscript',
    subscript: 'subscript',
    code: 'code',
    link: 'link',
    quote: 'quote',
    heading: 'heading',
    subheading: 'subheading',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    'bullet-list': 'bullet-list',
    'numbered-list': 'numbered-list',
    'paragraph-align-left': 'paragraph-align-left',
    'paragraph-align-center': 'paragraph-align-center',
    'paragraph-align-right': 'paragraph-align-right',
    'paragraph-align-justify': 'paragraph-align-justify',
    indent: 'indent',
    outdent: 'outdent',
    table: 'table',
    'full-width': 'full-width',
    'align-left': 'align-left',
    'align-right': 'align-right',
    close: 'close',
    add: 'add',
    'add-small': 'add-small',
    check: 'check',
    'check-small': 'check-small',
    'left-chevron': 'left-chevron',
    'right-chevron': 'right-chevron',
    'chevron-dropdown': 'chevron-dropdown',
    'chevron-down': 'chevron-dropdown',
    'chevron-up': 'chevron-up',
    'dropdown-arrow': 'dropdown-arrow',
    switch: 'switch',
    order: 'order',
    drag: 'drag',
    newsletter: 'newsletter',
    email: 'email',
    invite: 'invite',
    phone: 'phone',
    mention: 'mention',
    rss: 'rss',
    twitter: 'twitter',
    facebook: 'facebook',
    google: 'google',
    gplus: 'google',
    linkedin: 'linkedin',
    youtube: 'youtube',
    vimeo: 'vimeo',
    instagram: 'instagram',
    soundcloud: 'soundcloud',
    pinterest: 'pinterest',
    slack: 'slack',
    buffer: 'buffer',
    pressly: 'pressly',
    embed: 'embed',
    'num-list': 'num-list',
    'zoom-out': 'zoom-out',
    'zoom-in': 'zoom-in',
    'paragraph-styling': 'paragraph-styling',
    'remove-styling': 'remove-styling',
    'expand-link': 'expand-link',
    multishare: 'multishare',
    'text-scale': 'text-scale',
    'to-arrow': 'to-arrow',
    media: 'media',
    repost: 'repost',
    survey: 'survey',
    discussion: 'discussion',
    hot: 'hot',
  }

  render() {
    const { className, color, style, type, size, onClick, ...rest } = this.props

    return (
      <Container
        onClick={onClick}
        style={{
          cursor: onClick ? 'pointer' : undefined,
          fontSize: size,
          color,
          ...style,
        }}
        className={classNames(
          'icon',
          'px-icon',
          `px-icon-${type}`,
          { 'px-icon-has-link': typeof onClick === 'function' },
          className
        )}
        {...getA11yProps(rest)}
      />
    )
  }
}

const getA11yProps = (props: Object) =>
  pickBy((val: string, key: string) => key.startsWith('aria-'), props)

const Container = styled.span`
  display: inline-block;
  font-family: 'pressly-icons';
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  vertical-align: middle;
  text-align: center;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  z-index: 1;

  .px-icon-has-link {
    cursor: pointer;
  }

  &.px-icon-menu::before {
    content: '\ue600';
  }

  &.px-icon-back::before {
    content: '\ue601';
  }

  &.px-icon-next::before {
    content: '\ue80e';
  }

  &.px-icon-options::before {
    content: '\ue602';
  }

  &.px-icon-stream::before {
    content: '\ue603';
  }

  &.px-icon-inbox::before {
    content: '\ue917';
  }

  &.px-icon-bookmark::before {
    content: '\ue604';
  }

  &.px-icon-feeds::before {
    content: '\ue927';
  }

  &.px-icon-megaphone::before {
    content: '\ue91c';
  }

  &.px-icon-amplify::before {
    content: '\ue929';
  }

  &.px-icon-bell::before {
    content: '\ue605';
  }

  &.px-icon-notify::before {
    content: '\ue90a';
  }

  &.px-icon-collections::before {
    content: '\ue905';
  }

  &.px-icon-search::before {
    content: '\ue606';
  }

  &.px-icon-search-small::before {
    content: '\ue607';
  }

  &.px-icon-compose::before {
    content: '\ue608';
  }

  &.px-icon-create::before {
    content: '\ue923';
  }

  &.px-icon-manage::before {
    content: '\ue60d';
  }

  &.px-icon-edit::before {
    content: '\ue614';
  }

  &.px-icon-submission::before {
    content: '\ue925';
  }

  &.px-icon-published::before {
    content: '\ue926';
  }

  &.px-icon-file::before {
    content: '\ue922';
  }

  &.px-icon-content::before {
    content: '\ue924';
  }

  &.px-icon-draft::before {
    content: '\ue904';
  }

  &.px-icon-like-old::before {
    content: '\ue609';
  }

  &.px-icon-comment-old::before {
    content: '\ue60a';
  }

  &.px-icon-view-old::before {
    content: '\ue92a';
  }

  &.px-icon-share-old::before {
    content: '\ue91e';
  }

  &.px-icon-like::before {
    content: '\ue958';
  }

  &.px-icon-comment::before {
    content: '\ue959';
  }

  &.px-icon-view::before {
    content: '\ue957';
  }

  &.px-icon-share::before {
    content: '\ue960';
  }

  &.px-icon-like-uncolored::before {
    content: '\ue953';
  }

  &.px-icon-comment-uncolored::before {
    content: '\ue954';
  }

  &.px-icon-view-uncolored::before {
    content: '\ue952';
  }

  &.px-icon-share-uncolored::before {
    content: '\ue955';
  }

  &.px-icon-bar-graph::before {
    content: '\ue956';
  }

  &.px-icon-networks::before {
    content: '\ue60b';
  }

  &.px-icon-hub::before {
    content: '\ue942';
  }

  &.px-icon-tags::before {
    content: '\ue90f';
  }

  &.px-icon-tag::before {
    content: '\ue916';
  }

  &.px-icon-template::before {
    content: '\ue921';
  }

  &.px-icon-team::before {
    content: '\ue60c';
  }

  &.px-icon-profile::before {
    content: '\ue800';
  }

  &.px-icon-user::before {
    content: '\ue918';
  }

  &.px-icon-design::before {
    content: '\ue60e';
  }

  &.px-icon-analytics::before {
    content: '\ue60f';
  }

  &.px-icon-integrations::before {
    content: '\ue611';
  }

  &.px-icon-accounts::before {
    content: '\ue612';
  }

  &.px-icon-settings::before {
    content: '\ue613';
  }

  &.px-icon-ctas::before {
    content: '\ue610';
  }

  &.px-icon-poll::before {
    content: '\ue903';
  }

  &.px-icon-grid::before {
    content: '\ue813';
  }

  &.px-icon-hubs::before {
    content: '\ue91a';
  }

  &.px-icon-addons::before {
    content: '\ue900';
  }

  &.px-icon-refresh::before {
    content: '\ue91f';
  }

  &.px-icon-resend::before {
    content: '\ue91d';
  }

  &.px-icon-responses::before {
    content: '\ue91b';
  }

  &.px-icon-feature::before {
    content: '\ue615';
  }

  &.px-icon-pin::before {
    content: '\ue928';
  }

  &.px-icon-hide-content::before {
    content: '\ue617';
  }

  &.px-icon-camera::before {
    content: '\ue620';
  }

  &.px-icon-no-image::before {
    content: '\ue80f';
  }

  &.px-icon-lock::before {
    content: '\ue616';
  }

  &.px-icon-add-image::before {
    content: '\ue906';
  }

  &.px-icon-lead-image::before {
    content: '\ue915';
  }

  &.px-icon-upload::before {
    content: '\ue810';
  }

  &.px-icon-download::before {
    content: '\ue920';
  }

  &.px-icon-target::before {
    content: '\ue811';
  }

  &.px-icon-delete::before {
    content: '\ue621';
  }

  &.px-icon-delete-small::before {
    content: '\ue90e';
  }

  &.px-icon-calendar::before {
    content: '\ue911';
  }

  &.px-icon-clock::before {
    content: '\ue912';
  }

  &.px-icon-time::before {
    content: '\ue913';
  }

  &.px-icon-warning::before {
    content: '\ue908';
  }

  &.px-icon-title::before {
    content: '\ue919';
  }

  &.px-icon-bold::before {
    content: '\ue92b';
  }

  &.px-icon-italic::before {
    content: '\ue92c';
  }

  &.px-icon-underline::before {
    content: '\ue92d';
  }

  &.px-icon-strikethrough::before {
    content: '\ue92e';
  }

  &.px-icon-superscript::before {
    content: '\ue937';
  }

  &.px-icon-subscript::before {
    content: '\ue938';
  }

  &.px-icon-code::before {
    content: '\ue92f';
  }

  &.px-icon-link::before {
    content: '\ue902';
  }

  &.px-icon-quote::before {
    content: '\ue930';
  }

  &.px-icon-heading::before {
    content: '\ue931';
  }

  &.px-icon-subheading::before {
    content: '\ue932';
  }
  &.px-icon-h1::before {
    font-family: 'arial';
    font-weight: 600;
    content: 'H1';
  }

  &.px-icon-h2::before {
    font-family: 'arial';
    font-weight: 600;
    content: 'H2';
  }
  &.px-icon-h3::before {
    font-family: 'arial';
    font-weight: 600;
    content: 'H3';
  }

  &.px-icon-bullet-list::before {
    content: '\ue907';
  }

  &.px-icon-numbered-list::before {
    content: '\ue939';
  }

  &.px-icon-paragraph-align-left::before {
    content: '\ue93a';
  }

  &.px-icon-paragraph-align-center::before {
    content: '\ue93b';
  }

  &.px-icon-paragraph-align-right::before {
    content: '\ue93c';
  }

  &.px-icon-paragraph-align-justify::before {
    content: '\ue93d';
  }

  &.px-icon-indent::before {
    content: '\ue93e';
  }

  &.px-icon-outdent::before {
    content: '\ue93f';
  }

  &.px-icon-table::before {
    content: '\ue940';
  }

  &.px-icon-full-width::before {
    content: '\ue933';
  }

  &.px-icon-align-left::before {
    content: '\ue941';
  }

  &.px-icon-align-right::before {
    content: '\ue934';
  }

  &.px-icon-close::before {
    content: '\ue618';
  }

  &.px-icon-add::before {
    content: '\ue619';
  }

  &.px-icon-add-small::before {
    content: '\ue61a';
  }

  &.px-icon-check::before {
    content: '\ue61b';
  }

  &.px-icon-check-small::before {
    content: '\ue61c';
  }

  &.px-icon-left-chevron::before {
    content: '\ue801';
  }

  &.px-icon-right-chevron::before {
    content: '\ue61d';
  }

  &.px-icon-chevron-dropdown::before {
    content: '\ue61e';
  }

  &.px-icon-chevron-up::before {
    content: '\ue901';
  }

  &.px-icon-dropdown-arrow::before {
    content: '\ue61f';
  }

  &.px-icon-switch::before {
    content: '\ue909';
  }

  &.px-icon-order::before {
    content: '\ue910';
  }

  &.px-icon-drag::before {
    content: '\ue936';
  }

  &.px-icon-newsletter::before {
    content: '\ue943';
  }

  &.px-icon-email::before {
    content: '\ue90d';
  }

  &.px-icon-invite::before {
    content: '\ue90b';
  }

  &.px-icon-phone::before {
    content: '\ue935';
  }

  &.px-icon-mention::before {
    content: '\ue90c';
  }

  &.px-icon-rss::before {
    content: '\ue802';
  }

  &.px-icon-twitter::before {
    content: '\ue806';
  }

  &.px-icon-facebook::before {
    content: '\ue804';
  }

  &.px-icon-gplus::before {
    content: '\ue805';
  }

  &.px-icon-google::before {
    content: '\ue805';
  }

  &.px-icon-linkedin::before {
    content: '\ue812';
  }

  &.px-icon-youtube::before {
    content: '\ue807';
  }

  &.px-icon-vimeo::before {
    content: '\ue808';
  }

  &.px-icon-instagram::before {
    content: '\ue809';
  }

  &.px-icon-soundcloud::before {
    content: '\ue80a';
  }

  &.px-icon-pinterest::before {
    content: '\ue80b';
  }

  &.px-icon-slack::before {
    content: '\ue80c';
  }

  &.px-icon-buffer::before {
    content: '\ue914';
  }

  &.px-icon-pressly::before {
    content: '\ue80d';
  }

  &.px-icon-embed::before {
    content: '\ue944';
  }

  &.px-icon-num-list::before {
    content: '\ue945';
  }

  &.px-icon-zoom-out::before {
    content: '\ue946';
  }

  &.px-icon-zoom-in::before {
    content: '\ue947';
  }

  &.px-icon-paragraph-styling::before {
    content: '\ue948';
  }

  &.px-icon-remove-styling::before {
    content: '\ue949';
  }

  &.px-icon-expand-link::before {
    content: '\ueb7e';
  }

  &.px-icon-multishare::before {
    content: '\ue94a';
  }

  &.px-icon-text-scale::before {
    content: '\ue94b';
  }

  &.px-icon-to-arrow::before {
    content: '\ue94c';
  }

  &.px-icon-media::before {
    content: '\ue94d';
  }

  &.px-icon-repost::before {
    content: '\ue94e';
  }

  &.px-icon-survey::before {
    content: '\ue94f';
  }

  &.px-icon-discussion::before {
    content: '\ue950';
  }

  &.px-icon-hot::before {
    content: '\ue951';
  }
`

export default Icon
